import { ref, onBeforeMount } from 'vue'
import { useStorage } from '@vueuse/core'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'

import { checkSso, login } from '@auth/api/login.api'

export function useSharedLoginProps () {
  return {
    action: {
      type: String,
      default: '/login/j_spring_security_check',
    },
    title: {
      type: String,
      default: 'Sign in to your account',
    },
    usernameLabel: {
      type: String,
      default: 'Username',
    },
    dontRememberUsername: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: 'Sign In',
    },
    submitClasses: {
      type: String,
      default: '',
    },
    helpUrl: {
      type: String,
      default: '/forgot-password/',
    },
    helpMessage: {
      type: String,
      default: 'Need help signing in?',
    },
  }
}

export function useLogin (formSchema) {
  // form state
  const showPassword = ref(false)
  const isLoginError = ref(false)
  const errorMessage = ref(null)
  const showCaptcha = ref(false)
  const isSubmitting = ref(false)
  // local storage
  const username = useStorage('username', '')
  const rememberMe = useStorage('rememberMe', false)

  const DEFAULT_ERROR_MESSAGE = 'Invalid username or password'

  onBeforeMount(() => {
    if (isLoginError.value) {
      clearStoredFormData()
      setFieldValue('rememberMe', false)
    }
  })

  const { values, handleSubmit, setFieldValue } = useForm({
    validationSchema: toTypedSchema(z.object(formSchema.value)),
    initialValues: {
      j_username: username.value,
      rememberMe: rememberMe.value,
    },
  })

  const saveFormDataToStorage = () => {
    username.value = values.j_username
    rememberMe.value = values.rememberMe
  }

  const clearStoredFormData = () => {
    rememberMe.value = null
  }

  function redirectTo (url) {
    console.debug('Redirecting to', url)
    window.location.assign(url)
  }

  async function submitForm (action) {
    const formData = new FormData()
    formData.append('j_username', values.j_username)
    formData.append('j_password', values.j_password)
    if (showCaptcha.value) {
      formData.append('g-recaptcha-response', document.getElementById('captchaResponse')?.value)
    }

    try {
      isSubmitting.value = true
      const response = await login(action, formData)
      const redirect = response.redirect || '/'
      redirectTo(redirect)
    } catch (err) {
      const redirect = err.response?.data?.redirect
      if (redirect) redirectTo(redirect)
      else {
        isSubmitting.value = false
        isLoginError.value = true
        errorMessage.value = err.response?.data?.message || DEFAULT_ERROR_MESSAGE
        showCaptcha.value = err.response?.data?.show_captcha
      }
    }
  }

  return {
    errorMessage,
    isLoginError,
    isSubmitting,
    showCaptcha,
    showPassword,

    checkSso,
    clearStoredFormData,
    handleSubmit,
    login,
    saveFormDataToStorage,
    submitForm,
  }
}
